import { Draggable } from "@hello-pangea/dnd";
import { DragIndicatorRounded } from "@mui/icons-material";
import { Box, Card, Stack, styled, SxProps, useTheme } from "@mui/material";
import { ActionItemCardContent, ItemSkeleton } from "src/components";
import { useGetActionItemSummary, type UserDto } from "../../http";

const StyledStack = styled(Stack)<{ $isDraggable: boolean; $isDragging: boolean; $isPastView: boolean }>`
  transition: ${({ theme }) => theme.transitions.create("background-color")};
  background-color: ${({ $isDragging, $isPastView, theme }) =>
    $isDragging ? theme.palette.primary.transparent10 : $isPastView ? "transparent" : undefined};

  .dragHandle {
    opacity: 1;
    @media (pointer: fine) {
      opacity: ${({ $isDragging }) => ($isDragging ? 1 : 0)};
    }
  }

  &:hover {
    background-color: ${({ $isPastView, theme }) => ($isPastView ? "transparent" : theme.palette.primary.transparent5)};

    .dragHandle {
      opacity: 1;
    }
  }
`;

const DragHandle = styled(Box)<{ $isDraggable: boolean; $isDisabled: boolean }>`
  color: ${({ theme, $isDisabled }) => ($isDisabled ? "transparent" : theme.palette.text.secondary)};
  background-color: "transparent";
  @media (pointer: fine) {
    color: ${({ theme, $isDisabled }) => ($isDisabled ? "transparent" : theme.palette.primary.main)};
    background-color: ${({ theme, $isDisabled }) => ($isDisabled ? "transparent" : theme.palette.primary.transparent5)};
  }
  display: ${({ $isDraggable }) => ($isDraggable ? "flex" : "none")};
  align-self: stretch;
  align-items: center;
  justify-content: center;
  transition: ${({ theme }) => theme.transitions.create(["opacity"])};
  cursor: ${({ $isDisabled }) => ($isDisabled ? "default" : "pointer")};
  min-width: 20px;
`;

interface IBaseProps {
  actionItemGuid: string;
  canEditPlan: boolean;
  canEdit: boolean;
  showTeamChip?: boolean;
  isPastView?: boolean;
  isFocused?: boolean;
  onBlur?: () => void;
  onOwnershipChanged?: (actionItemGuid: string, actionItemDescription: string, removedOwners: UserDto[]) => void;
  sx?: SxProps;
}

type IProps = IBaseProps &
  (
    | {
        draggable: true;
        index: number;
      }
    | {
        draggable?: false;
        index?: never;
      }
  );

const ActionItemCard = (props: IProps) => {
  const { data: actionItem, isLoading } = useGetActionItemSummary(props.actionItemGuid);
  const theme = useTheme();

  const isDragDisabled = !props.draggable || !props.canEditPlan || props.isFocused;

  return isLoading || !actionItem ? (
    <ItemSkeleton variant="card" sx={{ pt: 0.5 }} />
  ) : props.draggable ? (
    <Draggable
      key={props.actionItemGuid}
      draggableId={props.actionItemGuid}
      index={props.index}
      isDragDisabled={isDragDisabled}
    >
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          sx={{
            zIndex: 2,
            border: `solid 1px ${theme.palette.line.main}`,
            backgroundColor: props.isPastView ? theme.palette.grey[100] : undefined,
            ...props.sx,
          }}
          elevation={snapshot.isDragging ? 1 : 0}
        >
          <StyledStack
            direction="row"
            sx={{ alignItems: "center", py: 0, width: "100%" }}
            $isDraggable={props.canEditPlan}
            $isDragging={snapshot.isDragging}
            $isPastView={props.isPastView ?? false}
          >
            <DragHandle
              className="dragHandle"
              $isDraggable={props.canEditPlan}
              $isDisabled={props.isFocused ?? false}
              {...provided.dragHandleProps}
            >
              <DragIndicatorRounded />
            </DragHandle>

            <ActionItemCardContent
              actionItemGuid={props.actionItemGuid}
              canEditPlan={props.canEditPlan}
              canEdit={props.canEdit}
              showTeamChip={props.showTeamChip}
              isPastView={props.isPastView}
              isFocused={props.isFocused}
              onBlur={props.onBlur}
              onOwnershipChanged={props.onOwnershipChanged}
              sx={{ pr: { xs: 0.5, sm: 1 }, pl: props.canEditPlan ? 0 : { xs: 0.5, sm: 1 } }}
            />
          </StyledStack>
        </Card>
      )}
    </Draggable>
  ) : (
    <Card sx={{ zIndex: 2, border: `solid 1px ${theme.palette.line.main}`, ...props.sx }} elevation={0}>
      <ActionItemCardContent
        actionItemGuid={props.actionItemGuid}
        canEditPlan={props.canEditPlan}
        canEdit={props.canEdit}
        showTeamChip={props.showTeamChip}
        isPastView={props.isPastView}
        isFocused={props.isFocused}
        onBlur={props.onBlur}
        onOwnershipChanged={props.onOwnershipChanged}
        sx={{ px: 0.5 }}
      />
    </Card>
  );
};

export { ActionItemCard };
